import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Subscription, timer } from 'rxjs';
import { switchMap, takeWhile } from 'rxjs/operators';
import { SelectItem } from 'src/app/core/models/responses/select-item';
import { AlertService } from 'src/app/services/alert.service';
import { DataService } from 'src/app/services/data.service';
import { PosDeviceService } from 'src/app/services/pos-device.service';
import { PosDevice, PosDeviceSaveRequest } from 'src/app/models/pos/pos-device';
import {
  PosDeviceProviderLabels,
  PosDeviceProviderType,
  PosPairingStatus,
  PosPairingStatusLabels,
} from 'src/app/models/enums/pos-enums';
import { extractErrorMessage } from '../../accounting/shared/e-invoice.helpers';

/** Eşleştirme kodu cihazda bu süre içinde onaylanmazsa ekran yoklamayı bırakır. */
const PAIRING_POLL_SECONDS = 3;
const PAIRING_TIMEOUT_SECONDS = 300;

/**
 * POS cihazları (pos-entegrasyonu.md K6, K10, K14) ve sağlayıcı ayarı.
 *
 * Akış: cihaz seri numarasıyla tanımlanır → "Eşleştir" cihaza özel kimliği üretir ve
 * bir kod döndürür → kod cihaz ekranında onaylanır, ekran onayı yoklar → POS kasası
 * POS Ayarları ekranında cihaza bağlanır. Cihaz kimliği ekrana hiç dönmez.
 */
@Component({
  selector: 'app-pos-devices',
  templateUrl: './pos-devices.component.html',
})
export class PosDevicesComponent implements OnInit, OnDestroy {
  breadCrumbItems = [{ label: 'Kasa' }, { label: 'POS Cihazları', active: true }];

  readonly providers = [PosDeviceProviderType.PavoUniCloud, PosDeviceProviderType.Mock];
  readonly providerLabels = PosDeviceProviderLabels;
  readonly pairingLabels = PosPairingStatusLabels;
  readonly PairingStatus = PosPairingStatus;

  activeTab = 1;
  devices: PosDevice[] = [];
  branches: SelectItem[] = [];
  loading = false;

  @ViewChild('deviceModal') deviceModalTemplate!: TemplateRef<any>;
  @ViewChild('pairingModal') pairingModalTemplate!: TemplateRef<any>;
  private deviceModalRef: NgbModalRef | null = null;

  form: PosDeviceSaveRequest = this.emptyForm();
  saving = false;
  formError: string | null = null;

  /** Eşleştirme penceresindeki cihaz — yoklama sonucu tazelenir. */
  pairingDevice: PosDevice | null = null;
  pairingError: string | null = null;
  pairingTimedOut = false;
  renewing = false;
  private pairingPoll?: Subscription;
  /** Satır bazında süren istek (kimlik üret, eşleştir, sil). */
  busyDeviceId: number | null = null;

  constructor(
    private service: PosDeviceService,
    private dataService: DataService,
    private alertService: AlertService,
    private modalService: NgbModal,
  ) {}

  ngOnInit(): void {
    this.load();
    this.dataService.getBranch({}).subscribe({ next: res => this.branches = res.data || [] });
  }

  ngOnDestroy(): void {
    this.stopPairingPoll();
  }

  load(): void {
    this.loading = true;
    this.service.list(false).subscribe({
      next: res => {
        this.devices = res.data || [];
        this.loading = false;
      },
      error: error => {
        this.alertService.showErrorsToast([extractErrorMessage(error, 'POS cihazları alınamadı.')]);
        this.loading = false;
      },
    });
  }

  pairingBadge(status: PosPairingStatus): string {
    switch (status) {
      case PosPairingStatus.Paired: return 'bg-success';
      case PosPairingStatus.Pending: return 'bg-warning';
      case PosPairingStatus.Revoked: return 'bg-danger';
      default: return 'bg-secondary';
    }
  }

  // ── Tanım ──────────────────────────────────────────────────────────────

  openDevice(device?: PosDevice): void {
    this.form = device
      ? {
          id: device.id,
          name: device.name,
          branchId: device.branchId,
          providerType: device.providerType,
          serialNo: device.serialNo,
          merchantUid: device.merchantUid,
          isActive: device.isActive,
        }
      : this.emptyForm();
    this.formError = null;
    this.deviceModalRef = this.modalService.open(this.deviceModalTemplate, { backdrop: 'static' });
  }

  get identityWillReset(): boolean {
    const device = this.devices.find(d => d.id === this.form.id);
    if (!device) return false;
    return device.serialNo !== (this.form.serialNo || '').trim().toUpperCase()
      || device.providerType !== +this.form.providerType
      || (device.merchantUid || null) !== (this.form.merchantUid?.trim() || null);
  }

  saveDevice(): void {
    const request: PosDeviceSaveRequest = {
      ...this.form,
      name: (this.form.name || '').trim(),
      serialNo: (this.form.serialNo || '').trim().toUpperCase(),
      merchantUid: this.form.merchantUid?.trim() || null,
      providerType: +this.form.providerType,
      branchId: this.form.branchId ? +this.form.branchId : null,
    };
    if (!request.name || !request.serialNo) {
      this.formError = 'Cihaz adı ve seri numarası zorunludur.';
      return;
    }

    this.saving = true;
    this.formError = null;
    this.service.save(request).subscribe({
      next: res => {
        this.saving = false;
        this.deviceModalRef?.close();
        this.alertService.showSuccess(request.id ? 'Cihaz güncellendi.' : 'Cihaz eklendi. Şimdi cihazı eşleştirin.');
        this.load();
        // Yeni cihaz: eşleştirme hemen önerilir.
        if (!request.id && res.data) this.startPairing(res.data);
      },
      error: error => {
        this.saving = false;
        this.formError = extractErrorMessage(error, 'Cihaz kaydedilemedi.');
      },
    });
  }

  deleteDevice(device: PosDevice): void {
    this.alertService
      .showConfirmAlert(`"${device.name}" cihazı silinecek. Geçmiş işlemler kayıtlı kalır.`, 'Evet, Sil', 'Vazgeç')
      .then(result => {
        if (!result.isConfirmed) return;
        this.busyDeviceId = device.id;
        this.service.delete(device.id).subscribe({
          next: () => {
            this.busyDeviceId = null;
            this.alertService.showSuccess('Cihaz silindi.');
            this.load();
          },
          error: error => {
            this.busyDeviceId = null;
            this.alertService.showErrorsToast([extractErrorMessage(error, 'Cihaz silinemedi.')]);
          },
        });
      });
  }

  /** Cihaz kimliğini yeniden üretir — sağlayıcı kimliği reddettiğinde ya da cihaz değiştiğinde. */
  provision(device: PosDevice): void {
    this.busyDeviceId = device.id;
    this.service.provision(device.id).subscribe({
      next: () => {
        this.busyDeviceId = null;
        this.alertService.showSuccess('Cihaz kimliği üretildi.');
        this.load();
      },
      error: error => {
        this.busyDeviceId = null;
        this.alertService.showErrorsToast([extractErrorMessage(error, 'Cihaz kimliği üretilemedi.')]);
      },
    });
  }

  // ── Eşleştirme ─────────────────────────────────────────────────────────

  /**
   * Yeni eşleştirme kodu: sağlayıcı aynı kaynak kimliğine bekleyen talep varken yenisini açmıyor,
   * eskisini döndürüyor. Talep cihaza düşmediyse ya da takıldıysa çıkış yolu bu. Eşleşmiş cihazda
   * mevcut eşleştirme geçersizleşir; onaylanana kadar cihaza satış gönderilemez.
   */
  renewPairing(device: PosDevice): void {
    const confirm = device.pairingStatus === PosPairingStatus.Paired
      ? this.alertService.showConfirmAlert(
          `"${device.name}" cihazının mevcut eşleştirmesi geçersiz olacak; yeni kod cihazda onaylanana kadar ` +
          'bu cihaza satış gönderilemez. Devam edilsin mi?', 'Yeni Kod Üret', 'Vazgeç')
      : Promise.resolve({ isConfirmed: true });
    confirm.then(result => {
      if (result.isConfirmed) this.startPairing(device, true);
    });
  }

  /** Eşleştirme penceresinden: kod cihaza düşmedi — yeni kodla yeniden dene. */
  renewInModal(): void {
    if (!this.pairingDevice) return;
    this.stopPairingPoll();
    this.pairingError = null;
    this.pairingTimedOut = false;
    this.renewing = true;
    this.service.renewPairing(this.pairingDevice.id).subscribe({
      next: res => {
        this.renewing = false;
        this.pairingDevice = res.data ?? this.pairingDevice;
        if (this.pairingDevice?.pairingStatus === PosPairingStatus.Pending) this.pollPairing(this.pairingDevice.id);
      },
      error: error => {
        this.renewing = false;
        this.pairingError = extractErrorMessage(error, 'Yeni eşleştirme kodu üretilemedi.');
      },
    });
  }

  startPairing(device: PosDevice, renew = false): void {
    this.busyDeviceId = device.id;
    this.pairingError = null;
    this.pairingTimedOut = false;
    const request = renew ? this.service.renewPairing(device.id) : this.service.startPairing(device.id);
    request.subscribe({
      next: res => {
        this.busyDeviceId = null;
        this.pairingDevice = res.data ?? device;
        const modal = this.modalService.open(this.pairingModalTemplate, { backdrop: 'static', centered: true });
        modal.result.finally(() => {
          this.stopPairingPoll();
          this.pairingDevice = null;
          this.load();
        });
        if (this.pairingDevice.pairingStatus === PosPairingStatus.Pending) this.pollPairing(this.pairingDevice.id);
      },
      error: error => {
        this.busyDeviceId = null;
        this.alertService.showErrorsToast([extractErrorMessage(error, 'Eşleştirme başlatılamadı.')]);
      },
    });
  }

  private pollPairing(deviceId: number): void {
    this.stopPairingPoll();
    const maxTicks = Math.ceil(PAIRING_TIMEOUT_SECONDS / PAIRING_POLL_SECONDS);
    let ticks = 0;
    this.pairingPoll = timer(PAIRING_POLL_SECONDS * 1000, PAIRING_POLL_SECONDS * 1000).pipe(
      takeWhile(() => ++ticks <= maxTicks),
      switchMap(() => this.service.refreshPairing(deviceId)),
    ).subscribe({
      next: res => {
        this.pairingDevice = res.data ?? this.pairingDevice;
        this.pairingError = null;
        if (this.pairingDevice?.pairingStatus !== PosPairingStatus.Pending) this.stopPairingPoll();
      },
      // Tek bir başarısız yoklama akışı bitirir; kullanıcı "Tekrar kontrol et" ile sürdürür.
      error: error => this.pairingError = extractErrorMessage(error, 'Eşleştirme durumu alınamadı.'),
      complete: () => {
        if (this.pairingDevice?.pairingStatus === PosPairingStatus.Pending) this.pairingTimedOut = true;
      },
    });
  }

  checkPairingAgain(): void {
    if (!this.pairingDevice) return;
    this.pairingTimedOut = false;
    this.pairingError = null;
    this.pollPairing(this.pairingDevice.id);
  }

  private stopPairingPoll(): void {
    this.pairingPoll?.unsubscribe();
    this.pairingPoll = undefined;
  }

  get isPolling(): boolean {
    return !!this.pairingPoll && !this.pairingPoll.closed;
  }

  private emptyForm(): PosDeviceSaveRequest {
    const branch = JSON.parse(localStorage.getItem('currentBranch') || 'null');
    return {
      id: null,
      name: '',
      branchId: branch?.id ?? null,
      providerType: PosDeviceProviderType.PavoUniCloud,
      serialNo: '',
      merchantUid: null,
      isActive: true,
    };
  }
}
